import React, { useEffect, useState, useCallback } from 'react';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { useLocation, useNavigate } from 'react-router-dom';
import { api_url } from '../config';
import api from '../utils/api';
import { useSelector } from 'react-redux';
import ErrorModal from '../components/ErrorModal';
import DeactivatePlanModal from '../components/DeactivatePlanModal';

// Import components
import CurrentPlanSection from '../components/Subscription/CurrentPlanSection';
import UpgradePlanSection from '../components/Subscription/UpgradePlanSection';
import { capitalizeFirstLetter, convertDate, calculateNextBillingDate } from '../components/Subscription/Utils';

const SubscriptionViewPage = () => {
    const [isPlanOpen, setIsPlanOpen] = useState(true);
    const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
    const [planData, setPlanData] = useState([]);
    const { id, token, name } = useSelector((state) => state.auth);
    const [errorModal, setErrorModal] = useState(false);
    const [deactivateModal, setDeactivateModal] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const [loadingPlanId, setLoadingPlanId] = useState(null);
    const [subscriptionData, setSubscriptionData] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();
    const { plan, subscription } = location?.state || {};

    // Fetch subscription data directly if it's missing important fields
    useEffect(() => {
        const fetchSubscriptionData = async () => {
            // Always fetch the complete subscription data for the given ID
            if (subscription && subscription.id) {
                try {
                    // First attempt - direct endpoint for a specific subscription
                    const response = await api.get(api_url + `/v1/subscriptions/${subscription.id}`, {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        }
                    });
                    
                    if (response?.data?.data) {
                        setSubscriptionData(response.data.data);
                        return; // Exit if successful
                    }
                    
                    // Second attempt - user subscriptions endpoint
                    const response2 = await api.get(api_url + `/v1/user/${id}/subscriptions`, {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        }
                    });
                    
                    if (response2?.data?.data && Array.isArray(response2.data.data)) {
                        // Find the matching subscription in the list
                        const matchingSub = response2.data.data.find(sub => 
                            sub.id === subscription.id || 
                            sub.stripe_id === subscription.stripe_id
                        );
                        
                        if (matchingSub) {
                            setSubscriptionData(matchingSub);
                            return; // Exit if successful
                        }
                    }
                } catch (error) {
                    console.error("Error fetching subscription details:", error);
                }
            }
        };
        
        fetchSubscriptionData();
    }, [id, token, subscription]);

    const handleOpenCurrentPlan = () => {
        setIsPlanOpen(!isPlanOpen);
        setIsUpgradeOpen(false);
    };

    const handleOpenUpgradePlan = () => {
        setIsPlanOpen(false);
        setIsUpgradeOpen(!isUpgradeOpen);
    };

    const handleGoto = () => {
        const url = process.env.REACT_APP_MAIN_URL;
        window.open(url, '_blank');
    };

    const fetchPlanData = useCallback(async (productTitle) => {
        setBtnLoader(true);
        setPlanData(null);

        const params = {
            page: 1,
            limit: 50,
            [`filter[app]`]: productTitle,
        };
        try {
            const response = await api.get(api_url + `/v1/user/${id}/plans`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }, params
            });
            const data = response.data.data;

            setPlanData(data);

        } catch (error) {
            console.error("Error fetching plan data:", error);
        } finally {
            setBtnLoader(false);
        }
    }, [id, token]);

    useEffect(() => {
        if (plan?.app) {
            fetchPlanData(plan.app);
        }
    }, [plan, fetchPlanData]);

    const getClientSecret = async (selectedPlan) => {
        setBtnLoader(true);
        setLoadingPlanId(selectedPlan.id);
        try {
            const data = await api.get(`${api_url}/v1/user/${id}/check-payment-method`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const client_secret = data?.data?.client_secret;
            const message = data?.data?.message;
            const user_name = data?.data?.name;
            const user_email = data?.data?.email;
            const pm_type = data?.data?.pm_type;
            const pm_last_four = data?.data?.pm_last_four;

            navigate('/subscribe',
                {
                    state: {
                        client_secret,
                        message,
                        user_name,
                        user_email,
                        pm_type,
                        pm_last_four,
                        plan: selectedPlan
                    }
                }
            );
        } catch (error) {
            console.error("Error getting client secret:", error);
            setErrorModal(true);
        } finally {
            setBtnLoader(false);
            setLoadingPlanId(null);
        }
    };

    // Use the directly fetched subscription data if available
    const subscriptionToUse = subscriptionData || subscription;

    // Add null check for plan and subscription
    if (!plan || !subscription) {
        return (
            <div className="p-4">
                <p>No subscription data available. Please go back and try again.</p>
                <button
                    onClick={() => navigate('/subscriptions')}
                    className="mt-4 bg-blue-500 text-white px-4 py-2 rounded">
                    Back to Subscriptions
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className='w-fit hover:underline mt-4'>
                <button
                    onClick={() => { navigate('/subscriptions'); }}
                    className='flex items-center rounded-md cursor-pointer hover:text-blue-600'>
                    <IoIosArrowRoundBack />
                    <p className='text-xs'>back</p>
                </button>
            </div>
            <div className='mt-4'>
                <p className='text-sm'>Hello {name},</p>
                <p className='text-sm'>Thank you for being a valued subscriber to {plan.app}</p>
            </div>

            {/* Current Plan Section */}
            <CurrentPlanSection 
                isPlanOpen={isPlanOpen}
                handleOpenCurrentPlan={handleOpenCurrentPlan}
                plan={plan}
                subscription={subscriptionToUse}
                name={name}
                handleGoto={handleGoto}
                setDeactivateModal={setDeactivateModal}
                convertDate={convertDate}
                calculateNextBillingDate={calculateNextBillingDate}
            />

            {/* Upgrade Plan Section */}
            <UpgradePlanSection 
                isUpgradeOpen={isUpgradeOpen}
                handleOpenUpgradePlan={handleOpenUpgradePlan}
                planData={planData}
                plan={plan}
                btnLoader={btnLoader}
                loadingPlanId={loadingPlanId}
                getClientSecret={getClientSecret}
                capitalizeFirstLetter={capitalizeFirstLetter}
            />

            {/* Modals */}
            {errorModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-90">
                    <div className="bg-white rounded-md min-h-max shadow-lg p-3">
                        <ErrorModal toggle={() => setErrorModal(false)} />
                    </div>
                </div>
            )}

            {deactivateModal && (
                <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full bg-gray-200 bg-opacity-70 backdrop-blur-sm">
                    <DeactivatePlanModal toggle={() => setDeactivateModal(false)} />
                </div>
            )}
        </div>
    );
};

export default SubscriptionViewPage;
